// TermsConditions.jsx
import React, { useEffect } from "react";
import "./TermsConditions.css";
import Footer from "./Footer";
import Navbar from "./Navbar";

const TermsConditions = () => {
  const currentYear = new Date().getFullYear();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <>
      <Navbar />
      <section className="terms" id="terms">
        <div className="terms-container">
          {/* Header */}
          <div className="terms-header">
            <span className="terms-tag">Legal</span>
            <h1 className="terms-title">
              Terms &amp; <span>Conditions</span>
            </h1>
            <p className="terms-subtitle">
              Please read these terms carefully before using our decoration services.
              By using our website and services, you agree to these terms.
            </p>
            <div className="terms-meta">
              <span className="terms-updated">
                <i className="fas fa-calendar-alt"></i> Last Updated: {currentYear}
              </span>
              <span className="terms-version">Version 1.0</span>
            </div>
          </div>

          {/* Content */}
          <div className="terms-content">
            <div className="terms-sidebar">
              <div className="terms-toc">
                <h3>Table of Contents</h3>
                <ul>
                  <li><a href="#section-1">1. Acceptance of Terms</a></li>
                  <li><a href="#section-2">2. Services Provided</a></li>
                  <li><a href="#section-3">3. Booking and Payment</a></li>
                  <li><a href="#section-4">4. Cancellation Policy</a></li>
                  <li><a href="#section-5">5. Refund Policy</a></li>
                  <li><a href="#section-6">6. Customer Responsibilities</a></li>
                  <li><a href="#section-7">7. Intellectual Property</a></li>
                  <li><a href="#section-8">8. Limitation of Liability</a></li>
                  <li><a href="#section-9">9. Indemnification</a></li>
                  <li><a href="#section-10">10. Governing Law</a></li>
                  <li><a href="#section-11">11. Changes to Terms</a></li>
                  <li><a href="#section-12">12. Contact Us</a></li>
                </ul>
              </div>
            </div>

            <div className="terms-main">
              {/* Section 1 */}
              <div className="terms-section" id="section-1">
                <h2>1. Acceptance of Terms</h2>
                <p>
                  By accessing and using the services provided by Atoz Events, you agree to be 
                  bound by these Terms and Conditions. If you do not agree with any part of these 
                  terms, please do not use our services.
                </p>
                <p>
                  These terms apply to all users, visitors, and customers who use our website 
                  or book our decoration services.
                </p>
              </div>

              {/* Section 2 */}
              <div className="terms-section" id="section-2">
                <h2>2. Services Provided</h2>
                <p>
                  Atoz Events provides professional decoration services for various events including:
                </p>
                <ul>
                  <li>Wedding decorations</li>
                  <li>Home decorations</li>
                  <li>Haldi and Mehndi decorations</li>
                  <li>Birthday and anniversary decorations</li>
                  <li>Corporate event decorations</li>
                  <li>Baby shower decorations</li>
                  <li>Engagement ceremony decorations</li>
                  <li>Stage decorations</li>
                </ul>
                <p>
                  We reserve the right to modify, suspend, or discontinue any service at any time 
                  without prior notice.
                </p>
              </div>

              {/* Section 3 */}
              <div className="terms-section" id="section-3">
                <h2>3. Booking and Payment</h2>
                <h3>Booking Process</h3>
                <ul>
                  <li>Bookings can be made through our website, phone, or WhatsApp</li>
                  <li>A confirmation will be sent after booking is received</li>
                  <li>All bookings are subject to availability</li>
                </ul>
                <h3>Payment Terms</h3>
                <ul>
                  <li>A <strong>50% advance payment</strong> is required to confirm the booking</li>
                  <li>The remaining 50% must be paid before or on the day of the event</li>
                  <li>We accept payments via bank transfer, UPI, and cash</li>
                  <li>All payments are non-refundable as per our cancellation policy</li>
                </ul>
              </div>

              {/* Section 4 */}
              <div className="terms-section" id="section-4">
                <h2>4. Cancellation Policy</h2>
                <p>
                  We understand that plans can change. Our cancellation policy is designed to be 
                  fair to both parties:
                </p>
                <ul>
                  <li>
                    <strong>15+ days before event:</strong> Full refund of advance payment
                  </li>
                  <li>
                    <strong>7-14 days before event:</strong> 50% refund of advance payment
                  </li>
                  <li>
                    <strong>Less than 7 days before event:</strong> No refund
                  </li>
                  <li>
                    <strong>On the day of event:</strong> No refund
                  </li>
                </ul>
                <p>
                  All cancellations must be communicated in writing via email or WhatsApp.
                </p>
              </div>

              {/* Section 5 */}
              <div className="terms-section" id="section-5">
                <h2>5. Refund Policy</h2>
                <p>
                  Refunds are processed according to our cancellation policy:
                </p>
                <ul>
                  <li>Refunds will be processed within 7-10 business days</li>
                  <li>Refunds will be made through the original payment method</li>
                  <li>Any transaction fees or charges will be deducted from the refund amount</li>
                  <li>No refund will be given for services already rendered</li>
                  <li>If we cancel the service due to unforeseen circumstances, a full refund will be provided</li>
                </ul>
              </div>

              {/* Section 6 */}
              <div className="terms-section" id="section-6">
                <h2>6. Customer Responsibilities</h2>
                <p>As a customer, you agree to:</p>
                <ul>
                  <li>Provide accurate and complete information when booking</li>
                  <li>Communicate any changes to event details promptly</li>
                  <li>Ensure proper access to the event venue for our team</li>
                  <li>Provide necessary permissions for decoration setup</li>
                  <li>Ensure the safety of our staff and equipment during setup</li>
                  <li>Report any issues or concerns immediately</li>
                </ul>
                <p>
                  Failure to fulfill these responsibilities may result in cancellation of services 
                  without refund.
                </p>
              </div>

              {/* Section 7 */}
              <div className="terms-section" id="section-7">
                <h2>7. Intellectual Property</h2>
                <p>
                  All content on our website, including text, images, designs, and logos, is the 
                  property of Atoz Events and is protected by copyright laws.
                </p>
                <p>
                  You may not:
                </p>
                <ul>
                  <li>Copy, reproduce, or distribute any content without our permission</li>
                  <li>Use our designs or ideas for commercial purposes</li>
                  <li>Modify or create derivative works from our content</li>
                  <li>Use our logo or branding without authorization</li>
                </ul>
                <p>
                  All decoration designs created by Atoz Events remain our intellectual property.
                </p>
              </div>

              {/* Section 8 */}
              <div className="terms-section" id="section-8">
                <h2>8. Limitation of Liability</h2>
                <p>
                  Atoz Events shall not be liable for any damages, losses, or expenses arising from:
                </p>
                <ul>
                  <li>Any delay or failure in providing services due to unforeseen circumstances</li>
                  <li>Damage to property caused by events beyond our control</li>
                  <li>Customer's failure to provide accurate information</li>
                  <li>Any third-party services or products used during the event</li>
                  <li>Any indirect, incidental, or consequential damages</li>
                </ul>
                <p>
                  Our maximum liability shall be limited to the amount paid for the services.
                </p>
              </div>

              {/* Section 9 */}
              <div className="terms-section" id="section-9">
                <h2>9. Indemnification</h2>
                <p>
                  You agree to indemnify, defend, and hold harmless Atoz Events, its directors, 
                  employees, and agents from and against any claims, liabilities, damages, losses, 
                  and expenses arising out of:
                </p>
                <ul>
                  <li>Your use of our services</li>
                  <li>Your violation of these terms</li>
                  <li>Your violation of any rights of third parties</li>
                  <li>Any damages caused by you or your guests during the event</li>
                </ul>
              </div>

              {/* Section 10 */}
              <div className="terms-section" id="section-10">
                <h2>10. Governing Law</h2>
                <p>
                  These Terms and Conditions shall be governed by and construed in accordance with 
                  the laws of India. Any disputes arising from these terms shall be subject to the 
                  exclusive jurisdiction of the courts in Gurugram, Haryana.
                </p>
                <p>
                  If any provision of these terms is found to be invalid or unenforceable, the 
                  remaining provisions shall remain in full force and effect.
                </p>
              </div>

              {/* Section 11 */}
              <div className="terms-section" id="section-11">
                <h2>11. Changes to Terms</h2>
                <p>
                  We reserve the right to update or modify these Terms and Conditions at any time 
                  without prior notice. Changes will be effective immediately upon posting on our 
                  website.
                </p>
                <p>
                  We encourage you to review these terms periodically. Your continued use of our 
                  services after any changes constitutes your acceptance of the updated terms.
                </p>
              </div>

              {/* Section 12 */}
              <div className="terms-section" id="section-12">
                <h2>12. Contact Us</h2>
                <p>
                  If you have any questions, concerns, or requests regarding these Terms and 
                  Conditions, please contact us:
                </p>
                <div className="terms-contact-details">
                  <div className="terms-contact-item">
                    <i className="fas fa-phone-alt"></i>
                    <div>
                      <strong>Phone:</strong> <a href="tel:7023332099">7023332099</a>
                    </div>
                  </div>
                  <div className="terms-contact-item">
                    <i className="fab fa-whatsapp"></i>
                    <div>
                      <strong>WhatsApp:</strong> <a href="https://wa.me/917023332099">7023332099</a>
                    </div>
                  </div>
                  <div className="terms-contact-item">
                    <i className="fas fa-envelope"></i>
                    <div>
                      <strong>Email:</strong> <a href="mailto:info@atozevents.in">info@atozevents.in</a>
                    </div>
                  </div>
                  <div className="terms-contact-item">
                    <i className="fas fa-map-marker-alt"></i>
                    <div>
                      <strong>Address:</strong> 720/5, Patel Nagar, Gurugram, Haryana 122001
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Note */}
          <div className="terms-footer-note">
            <p>
              © {currentYear} <span>Atoz Events</span>. All Rights Reserved. 
              Please read our terms carefully before booking.
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default TermsConditions;